export const extractYouTubeVideoId = (url: string | undefined | null): string | null => {
  if (url == null || typeof url !== 'string' || url.trim() === '') {
    return null;
  }

  const source = url.match(/\bsrc=(["'])(.*?)\1/i)?.[2] ?? url.trim();
  const normalizedSource = source.startsWith('//')
    ? `https:${source}`
    : /^(www\.|m\.|youtube\.com|youtu\.be)/i.test(source)
      ? `https://${source}`
      : source;

  const sanitizeVideoId = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const videoId = value.trim().split(/[?&#/\s]/)[0];
    return /^[a-zA-Z0-9_-]+$/.test(videoId) ? videoId : null;
  };

  try {
    const parsedUrl = new URL(normalizedSource);
    const hostname = parsedUrl.hostname.replace(/^(www\.|m\.)/, '');
    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);

    if (hostname === 'youtu.be') {
      return sanitizeVideoId(pathParts[0]);
    }

    if (hostname === 'youtube.com' || hostname.endsWith('.youtube.com')) {
      if (parsedUrl.searchParams.has('v')) {
        return sanitizeVideoId(parsedUrl.searchParams.get('v'));
      }

      if (['embed', 'v', 'shorts', 'live'].includes(pathParts[0])) {
        return sanitizeVideoId(pathParts[1]);
      }
    }

    if (hostname === 'youtube-nocookie.com' || hostname.endsWith('.youtube-nocookie.com')) {
      if (['embed', 'v'].includes(pathParts[0])) {
        return sanitizeVideoId(pathParts[1]);
      }
    }
  } catch {
    // Fall back to regex extraction for partial URLs and pasted snippets.
  }

  const patterns = [
    /(?:youtube(?:-nocookie)?\.com\/watch\?(?:[^"'\s]*&)?v=|youtu\.be\/)([^&"'\s]+)/,
    /youtube(?:-nocookie)?\.com\/(?:embed|v|shorts|live)\/([^?&"'\s]+)/,
  ];

  const match = patterns.map(pattern => url.match(pattern)).find(result => result?.[1]);

  return match ? sanitizeVideoId(match[1]) : null;
};

export const isValidYouTubeUrl = (url: string | undefined | null): boolean => {
  return extractYouTubeVideoId(url) !== null;
};

export const getYouTubeThumbnailUrl = (videoId: string): string => {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
};
